<template>
  <Paragraph v-if="content.filterHeadline">{{ content.filterHeadline }}</Paragraph>
  <FilterButtons
    v-if="content.showFilter"
    :all-label="content.filterAllLabel ?? 'Alle'"
    :filters="categoryFilterButtons"
    :show-all-filter="content.showAllFilter"
    class="mb-8 mt-4 md:mb-12 xl:mt-5"
    @filter-change="onFilter"
  />
  <div>
    <div class="md:responsive-gap grid gap-4 md:grid-cols-2">
      <EditableComponent
        v-for="childContent in visibleComponents"
        :key="childContent['@id']"
        :content="childContent"
      />
    </div>
  </div>
</template>

<script setup>
import { EditableComponent } from "@magnolia/vue-editor";
import { Paragraph } from "~/components/clarity-ui/Paragraph";
import useFilterTracking from "~/composables/tracking/useFilterTracking";
import FilterButtons from "~/templates/components/FilterButtons.vue";
import { mapNodes } from "~/utils/helper/magnolia";

const props = defineProps(["content"]);

const FILTER_TYPE = "category_filter";

const activeFilters = ref([]);

const uniqueFilters = mergeArraysOfObjectsToOneArray(mapNodes(props.content));
const categoryFilterButtons = [
  ...uniqueFilters?.map((filter) => ({ label: filter["value"], value: filter["@id"] })),
];

useFilterTracking({
  type: FILTER_TYPE,
  filters: categoryFilterButtons,
  activeFilters: activeFilters,
});

const visibleComponents = computed(() => {
  return activeFilters.value.length === 0
    ? mapNodes(props.content)
    : mapNodes(props.content).filter((node) =>
        node.filterTags?.some((filter) => activeFilters.value?.includes(filter["@id"])),
      );
});

const onFilter = (currentFilter) => {
  activeFilters.value = currentFilter;
};

function mergeArraysOfObjectsToOneArray(nodes) {
  /**
   * .flatMap flatten the filters arrays
   * .map convert each object to a string fpr comparison
   * .map convert each unique string back to an object
   * */
  const allFilterTagStrings = nodes.flatMap((node) => {
    // Ensure filterTags exists and is an array before processing
    if (Array.isArray(node?.filterTags)) {
      return node.filterTags.map(JSON.stringify);
    }
    return []; // Return empty array if filterTags is not an array
  });

  // Create a Set to get unique string representations of objects
  const uniqueFilterTagStrings = new Set(allFilterTagStrings);

  // Convert back to an array of objects
  return Array.from(uniqueFilterTagStrings).map(JSON.parse);
}
</script>
